"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CAPTURE_ATTACHMENT_PREFIX = exports.CAPTURE_BASE_URL = exports.CAPTURE_MODULE_PREFIX = void 0;
exports.isAppScriptContentType = isAppScriptContentType;
exports.captureAttachmentImages = captureAttachmentImages;
exports.createCaptureSourceQuery = createCaptureSourceQuery;
const database_identity_1 = require("@streamlain/database-identity");
const app_base_binding_assets_1 = require("./app-base-binding-assets");
const app_base_contract_1 = require("./app-base-contract");
const app_base_source_query_1 = require("./app-base-source-query");
/**
 * Prefix of the keys of a capture's import map. A capture runs its modules
 * from blob: URLs, which have no path a relative specifier could resolve
 * against; an absolute specifier with this prefix resolves through the map
 * from every module.
 */
exports.CAPTURE_MODULE_PREFIX = 'sl-capture:';
/**
 * Address a captured App is served below. Nothing answers there: the capture
 * has no network, so its bridge reads data through its host page and its
 * attachment addresses resolve to the images the capture carries.
 */
exports.CAPTURE_BASE_URL = 'https://app-capture.invalid/';
/** Whether an App file is a script; stylesheets and images are not. */
function isAppScriptContentType(contentType) {
    return /(?:java|ecma)script/u.test(contentType);
}
/**
 * Prefix of the attachment addresses a captured App builds, through its
 * bridge or the page renderer: `<prefix><source>/<row>/<column>/<file>`.
 */
exports.CAPTURE_ATTACHMENT_PREFIX = `${exports.CAPTURE_BASE_URL}_app-attachment/`;
/** The images the attachment cells of `rows` name, each once. */
function captureAttachmentImages(columns, rows) {
    const attachmentColumns = columns.filter(column => column.type === 'attachment');
    const images = new Map();
    for (const row of rows)
        for (const column of attachmentColumns)
            for (const file of (0, app_base_binding_assets_1.normalizeAppBaseAttachmentFiles)(row.cells[column.id]?.value))
                if (file.mime.startsWith('image/'))
                    images.set(file.blobId, {
                        blobId: file.blobId,
                        mime: file.mime,
                        ...(file.size === undefined ? {} : { size: file.size }),
                    });
    return [...images.values()];
}
/**
 * Answers the App queries of a capture over its fixed rows, with the executor
 * every other host runs: rows as the capture carries them, in row shards
 * filled as the database writer fills them, so a large source stays within
 * the chunk budget of the executor. A capture has no viewer, so a filter on
 * the rows of the viewer finds none. A source outside the capture is
 * unavailable.
 */
function createCaptureSourceQuery(sources) {
    const readers = new Map();
    const readerOf = (sourceKey) => {
        const known = readers.get(sourceKey);
        if (known)
            return known;
        if (!Object.prototype.hasOwnProperty.call(sources, sourceKey))
            throw new app_base_source_query_1.AppBaseSourceQueryError('APP_BASE_SOURCE_UNAVAILABLE', 'App source is not part of this capture', { sourceKey });
        const { columns, rows } = sources[sourceKey];
        const schemaKey = (0, app_base_contract_1.appBaseSha256Hex)((0, app_base_source_query_1.appBaseQueryIdentity)(columns));
        const shards = captureRowShards(sourceKey, rows).map((shardRows, index) => ({
            shardId: `capture-${index}`,
            revision: (0, app_base_contract_1.appBaseSha256Hex)((0, app_base_source_query_1.appBaseQueryIdentity)([sourceKey, columns, shardRows])),
            rows: shardRows,
        }));
        const byId = new Map(shards.map(shard => [shard.shardId, shard]));
        const shardOfRow = new Map();
        for (const shard of shards)
            for (const row of shard.rows)
                if (!shardOfRow.has(row.id))
                    shardOfRow.set(row.id, shard.shardId);
        const reader = {
            workspaceId: 'immutable-capture',
            accessScope: (0, app_base_contract_1.appBaseSha256Hex)((0, app_base_source_query_1.appBaseQueryIdentity)([sourceKey, shards.map(shard => shard.revision)])),
            exactRowCounts: true,
            resultSource: 'local-shards',
            index: {
                sourceKey,
                schemaVersion: 1,
                schemaKey,
                totalRows: rows.length,
                columns,
                shards: shards.map(({ shardId, revision, rows: shardRows }) => ({
                    shardId,
                    revision,
                    rowCount: shardRows.length,
                })),
            },
            selectRowShards: async (rowIds) => [
                ...new Set(rowIds.flatMap(id => shardOfRow.get(id) ?? [])),
            ],
            readChunk: async (shardId) => {
                const shard = byId.get(shardId);
                return {
                    sourceKey,
                    shardId,
                    revision: shard.revision,
                    schemaKey,
                    rows: shard.rows,
                };
            },
        };
        readers.set(sourceKey, reader);
        return reader;
    };
    const answer = async (sourceKey, raw) => {
        const reader = readerOf(sourceKey);
        const parsed = (0, app_base_source_query_1.parsePortableAppBaseSourceQuery)(raw, reader.index.columns);
        // Linked rows are read from the captured origin, as this capture reads it.
        const query = await (0, app_base_source_query_1.resolveAppBaseLinkedFrom)(parsed.owner ? (0, app_base_source_query_1.withAppBaseOwnedRows)(parsed, []) : parsed, answer);
        const revisionVector = reader.index.shards.map(shard => ({
            shardId: shard.shardId,
            revision: shard.revision,
        }));
        return (0, app_base_source_query_1.executePortableAppBaseSourceQueryWindows)({
            source: reader,
            query,
            revision: (0, app_base_contract_1.appBaseSha256Hex)((0, app_base_source_query_1.appBaseQueryIdentity)([reader.accessScope, query])),
            revisionVector,
            maxWorkingBytes: 4 * 1024 * 1024,
        });
    };
    return answer;
}
/**
 * The rows in row shards as the database writer places them: each shard is
 * filled up to the target size of a row shard, and a row above the hard limit
 * of a shard is refused as the platform refuses it.
 */
function captureRowShards(sourceKey, rows) {
    const encoder = new TextEncoder();
    const sizes = new Map(rows.map(row => [row, encoder.encode(JSON.stringify(row)).length]));
    try {
        const shards = (0, database_identity_1.partitionDatabaseRowShardRows)(rows, database_identity_1.DATABASE_ROW_SHARD_TARGET_BYTES, database_identity_1.DATABASE_ROW_SHARD_HARD_LIMIT_BYTES, 
        // A JSON array of the rows: brackets, commas and each row.
        group => group.reduce((bytes, row) => bytes + sizes.get(row) + 1, 1));
        return shards.length ? shards : [[]];
    }
    catch (error) {
        if (error instanceof RangeError)
            throw (0, app_base_source_query_1.appBaseSourceUnavailableError)(sourceKey, 'stored_value_too_large');
        throw error;
    }
}
